import { rtLogService } from '@rammetec/core';
import { repositoryFactory, DataSourceStrategy, CacheStrategy, createWriteBatch, } from '@rammetec/data-access';
import { PROFILE_DATA, USER_PROFILE } from '../types/profile.js';
import { OrgScopedCrudService } from './OrgScopedCrudService.js';
import { ChildCrudService } from './ChildCrudService.js';
import { organizationUserService } from './organizationUserService.js';
class ProfileService {
    constructor() {
        this.crud = new OrgScopedCrudService({
            collection: 'profile',
            entity: PROFILE_DATA,
            dataSource: DataSourceStrategy.Firestore,
            cache: CacheStrategy.InMemory,
            lifecycle: true,
            audited: true,
        });
    }
    async userProfilesCrud(orgId, userId) {
        const repository = await repositoryFactory.getRepository(`org/${orgId}/users/${userId}/profiles`, USER_PROFILE, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
        return new ChildCrudService(repository, {
            entity: USER_PROFILE,
            auditable: false,
            audited: true,
        });
    }
    /**
     * Own-profile writes historically skip inclusion/lastModified stamps — preserve
     * that while still recording field audit (auditable: false, audited: true).
     */
    async unstampedProfileCrud(orgId) {
        const repository = await repositoryFactory.getRepository(`org/${orgId}/profile`, PROFILE_DATA, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
        return new ChildCrudService(repository, {
            entity: PROFILE_DATA,
            auditable: false,
            audited: true,
        });
    }
    /**
     * Creates a new profile in an organization.
     * If no ID is provided, Firestore will auto-generate one.
     * @returns The created ProfileData (with the generated id)
     */
    async createProfile(orgId, profileData, opts) {
        try {
            const created = await this.crud.create(orgId, {
                ...(profileData.id ? { id: profileData.id } : {}),
                name: profileData.name,
                abbreviation: profileData.abbreviation,
                ...(profileData.userId ? { userId: profileData.userId } : {}),
                ...(profileData.grants != null ? { grants: profileData.grants } : {}),
                ...(profileData.flatModules != null
                    ? { flatModules: profileData.flatModules }
                    : {}),
                inactivationDate: profileData.inactivationDate ?? null,
            }, undefined, { audit: opts?.audit });
            rtLogService.info('Profile created', { orgId, profileId: created.id, name: profileData.name });
            return created;
        }
        catch (error) {
            rtLogService.error('Error creating profile', { orgId, profileData, error });
            throw error;
        }
    }
    /**
     * Assigns a user to a profile within an organization.
     * Creates a record in org/<orgId>/users/<userId>/profiles/<id>
     * Field audit lands on the org-user aggregate (request 0257 §3.3).
     */
    async assignUserToProfile(orgId, userId, profileId, opts) {
        try {
            const batch = createWriteBatch();
            const audit = await organizationUserService.beginUserAudit(orgId, userId, opts?.audit);
            const links = await this.userProfilesCrud(orgId, userId);
            await links.create({
                id: `${userId}_${profileId}`,
                userId,
                profileId,
            }, { batch, audit });
            await organizationUserService.commitUserAuditBatch(orgId, batch, audit);
            rtLogService.info('User assigned to profile', { orgId, userId, profileId });
        }
        catch (error) {
            rtLogService.error('Error assigning user to profile', { orgId, userId, profileId, error });
            throw error;
        }
    }
    async getProfilesByOrganization(orgId) {
        try {
            return await this.crud.list(orgId);
        }
        catch (error) {
            rtLogService.error('Error fetching profiles by organization', { orgId, error });
            throw error;
        }
    }
    async getProfileData(orgId, profileId) {
        try {
            return await this.crud.getById(orgId, profileId);
        }
        catch (error) {
            rtLogService.error('Error fetching profile data', { orgId, profileId, error });
            throw error;
        }
    }
    async getUserProfileIds(orgId, userId) {
        try {
            const links = await this.userProfilesCrud(orgId, userId);
            const userProfiles = await links.getAll();
            return userProfiles.map((up) => up.profileId);
        }
        catch (error) {
            rtLogService.error('Error fetching user profile IDs', { orgId, userId, error });
            throw error;
        }
    }
    async getUserProfilesData(orgId, userId) {
        try {
            const profileIds = await this.getUserProfileIds(orgId, userId);
            if (profileIds.length === 0) {
                return [];
            }
            const profilePromises = profileIds.map((profileId) => this.getProfileData(orgId, profileId));
            const results = await Promise.allSettled(profilePromises);
            const profiles = [];
            results.forEach((result) => {
                if (result.status === 'fulfilled' && result.value) {
                    profiles.push(result.value);
                }
                else if (result.status === 'rejected') {
                    rtLogService.warn('Failed to fetch a profile, it might be invalid or deleted.', {
                        reason: result.reason,
                    });
                }
            });
            return profiles;
        }
        catch (error) {
            rtLogService.error('Error fetching user profiles data', { orgId, userId, error });
            throw error;
        }
    }
    async updateProfile(orgId, profileId, data) {
        try {
            await this.crud.update(orgId, profileId, data);
            rtLogService.info('Profile updated', { orgId, profileId });
        }
        catch (error) {
            rtLogService.error('Error updating profile', { orgId, profileId, error });
            throw error;
        }
    }
    async deleteProfile(orgId, profileId) {
        try {
            await this.crud.delete(orgId, profileId);
            rtLogService.info('Profile deleted', { orgId, profileId });
        }
        catch (error) {
            rtLogService.error('Error deleting profile', { orgId, profileId, error });
            throw error;
        }
    }
    async clearProfileCache(orgId) {
        try {
            if (orgId) {
                const profileRepository = await repositoryFactory.getRepository(`org/${orgId}/profile`, PROFILE_DATA, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
                await profileRepository.invalidateCache();
            }
        }
        catch (error) {
            rtLogService.error('Error clearing profile cache', { orgId, error });
            throw error;
        }
    }
    /**
     * Replaces all profile links for a user. Deletes + creates share one groupId on
     * the org-user aggregate (request 0257).
     */
    async setUserProfiles(orgId, userId, profileIds) {
        try {
            const batch = createWriteBatch();
            const audit = await organizationUserService.beginUserAudit(orgId, userId);
            const links = await this.userProfilesCrud(orgId, userId);
            const existingProfiles = await links.getAll();
            for (const profile of existingProfiles) {
                await links.delete(profile.id, { batch, audit, before: profile });
            }
            for (const profileId of profileIds) {
                await links.create({
                    id: `${userId}_${profileId}`,
                    userId,
                    profileId,
                }, { batch, audit });
            }
            await organizationUserService.commitUserAuditBatch(orgId, batch, audit);
            rtLogService.info('User profiles updated', { orgId, userId, profileIds });
        }
        catch (error) {
            rtLogService.error('Error setting user profiles', { orgId, userId, profileIds, error });
            throw error;
        }
    }
    async getUserOwnProfile(orgId, userId) {
        try {
            const allProfiles = await this.crud.list(orgId);
            return allProfiles.find((p) => p.userId === userId) || null;
        }
        catch (error) {
            rtLogService.error('Error fetching user own profile', { orgId, userId, error });
            throw error;
        }
    }
    async saveUserOwnProfile(orgId, userId, profileData) {
        try {
            const existingProfile = await this.getUserOwnProfile(orgId, userId);
            const writes = await this.unstampedProfileCrud(orgId);
            const links = await this.userProfilesCrud(orgId, userId);
            if (existingProfile) {
                const profileId = existingProfile.id;
                const updatedProfile = {
                    ...existingProfile,
                    name: profileData.name,
                    abbreviation: profileData.abbreviation,
                };
                const profileBatch = createWriteBatch();
                const profileAudit = await this.crud.beginAggregateAudit(orgId, {
                    id: profileId,
                    version: null,
                    record: updatedProfile,
                });
                await writes.update(profileId, {
                    name: profileData.name,
                    abbreviation: profileData.abbreviation,
                }, { batch: profileBatch, audit: profileAudit, before: existingProfile });
                await this.crud.commitAggregateBatch(orgId, profileBatch, profileAudit);
                // Preserve pre-funnel try/catch: getById returning null does not add the link.
                try {
                    await links.getById(`${userId}_${profileId}`);
                }
                catch {
                    const linkBatch = createWriteBatch();
                    const userAudit = await organizationUserService.beginUserAudit(orgId, userId);
                    await links.create({
                        id: `${userId}_${profileId}`,
                        userId,
                        profileId,
                    }, { batch: linkBatch, audit: userAudit });
                    await organizationUserService.commitUserAuditBatch(orgId, linkBatch, userAudit);
                }
                return updatedProfile;
            }
            const profileId = `user_${userId}`;
            const newProfile = {
                id: profileId,
                name: profileData.name,
                abbreviation: profileData.abbreviation,
                userId,
            };
            const profileBatch = createWriteBatch();
            const profileAudit = await this.crud.beginAggregateAudit(orgId, {
                id: profileId,
                version: null,
                record: newProfile,
            });
            await writes.create(newProfile, { batch: profileBatch, audit: profileAudit });
            await this.crud.commitAggregateBatch(orgId, profileBatch, profileAudit);
            const linkBatch = createWriteBatch();
            const userAudit = await organizationUserService.beginUserAudit(orgId, userId);
            await links.create({
                id: `${userId}_${profileId}`,
                userId,
                profileId,
            }, { batch: linkBatch, audit: userAudit });
            await organizationUserService.commitUserAuditBatch(orgId, linkBatch, userAudit);
            return newProfile;
        }
        catch (error) {
            rtLogService.error('Error saving user own profile', { orgId, userId, profileData, error });
            throw error;
        }
    }
    async deleteUserOwnProfile(orgId, userId) {
        try {
            const ownProfile = await this.getUserOwnProfile(orgId, userId);
            if (!ownProfile) {
                return;
            }
            const links = await this.userProfilesCrud(orgId, userId);
            try {
                const userProfileId = `${userId}_${ownProfile.id}`;
                const before = await links.getById(userProfileId);
                if (before) {
                    const linkBatch = createWriteBatch();
                    const userAudit = await organizationUserService.beginUserAudit(orgId, userId);
                    await links.delete(userProfileId, { batch: linkBatch, audit: userAudit, before });
                    await organizationUserService.commitUserAuditBatch(orgId, linkBatch, userAudit);
                }
            }
            catch {
                // User profile might not exist, that's okay
            }
            await this.crud.delete(orgId, ownProfile.id);
            rtLogService.info('User own profile deleted', {
                orgId,
                userId,
                profileId: ownProfile.id,
            });
        }
        catch (error) {
            rtLogService.error('Error deleting user own profile', { orgId, userId, error });
            throw error;
        }
    }
}
export const profileService = new ProfileService();
//# sourceMappingURL=profileService.js.map